import { useState } from 'react';
import {
  HiOutlineUserCircle,
  HiOutlineClipboardDocumentCheck,
  HiOutlineCog6Tooth,
} from 'react-icons/hi2';
import { useNavigate } from 'react-router-dom';
import { Box, Flex, Grid, Text } from '@chakra-ui/react';
import { DrawerComponent } from '@themes/aegix';
import { ProfileDrawerShell, type ProfileTab } from '@tiaki/profile-ui';
import { useAuthActions, useUser } from '@aegix/auth-identity';
import { useUsers } from '../hooks/queries';
import { deregisterSmsPush } from '../push/registerPush';
import type { SMSUser } from '../lib/sms-auth';
import { useTranslation } from 'react-i18next';
import ProfileDetails from './profile';
import { ProfileContact } from './profile/ProfileContact';
import { ProfileNotifications } from './profile/ProfileNotifications';
import { AssignmentsSection } from './profile/AssignmentsSection';
import { ProfileSubscriptionsEditor } from './profile/ProfileSubscriptionsEditor';
import { ResetPasswordButton } from './profile/ResetPasswordButton';
import { LanguageSelector } from './LanguageSelector';

interface ProfileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

/** A site picked on the Assignments tab to open the per-site sub-drawer. */
interface SelectedSite {
  id: number;
  name: string;
}

/**
 * ProfileDrawer — single-layer profile UX, rendered from the shared
 * `@tiaki/profile-ui` `ProfileDrawerShell`.
 *
 * Reached in one click from the header profile icon. Hosts three view tabs
 * (Personal / Assignments / Settings) plus a destructive Sign Out row
 * at the bottom. The shell owns the drawer chrome + tab state + Sign Out
 * footer; each tab's app-specific content is injected via its `render()`
 * slot. Reunification stays an SMS-only slot inside the Assignments tab.
 *
 * Two pieces sit outside the tab bodies:
 *   - Reset Password lives in the drawer FOOTER (above Sign Out) via the
 *     shell's `footerSlot` — the self-contained `ResetPasswordButton`.
 *   - Sites on the Assignments tab are selectable: picking one swaps the
 *     drawer body for a per-site subscriptions sub-drawer (mirroring AIM's
 *     site detail view, minus the default-location concept SMS doesn't have).
 *     The active tab is held HERE so it survives the sub-drawer swap that
 *     unmounts the shell.
 */
export function ProfileDrawer({ isOpen, onClose }: ProfileDrawerProps) {
  const user = useUser<SMSUser>();
  const { logout } = useAuthActions<SMSUser>();
  const navigate = useNavigate();

  const { data: currentUser, isLoading: isLoadingCurrentUser } = useUsers(
    user ? { ids: String(user.id) } : { ids: '' },
  );
  const userDetails = currentUser?.results[0];

  // Per-site subscriptions sub-drawer (mirrors AIM). When a site is selected on
  // the Assignments tab the shell is swapped for a detail view scoped to that
  // site; SMS has no default-location, so only the subscriptions render.
  const [selectedSite, setSelectedSite] = useState<SelectedSite | null>(null);

  // Active tab is held here (not in the shell) so it survives the sub-drawer
  // swap, which unmounts the shell — without this the shell would reset to its
  // first tab on back. The shell becomes controlled via `activeTab`.
  const [activeTab, setActiveTab] = useState<string>('personal');

  const handleSelectSite = (site: SelectedSite) => {
    setSelectedSite(site);
  };

  const handleBackToProfile = () => {
    setSelectedSite(null);
  };

  const handleClose = () => {
    onClose();
    setSelectedSite(null);
  };

  const handleSignOut = () => {
    handleClose();
    void (async () => {
      // Deregister this browser's FCM token BEFORE the session
      // tears down (the call rides the authenticated transport). Best-effort
      // inside deregisterSmsPush: a push-side failure never blocks logout.
      await deregisterSmsPush();
      await logout();
    })();
    navigate('/login');
  };

  const { t } = useTranslation();
  const tabs: ProfileTab[] = [
    {
      value: 'personal',
      label: t('profile.personal', 'Personal'),
      icon: HiOutlineUserCircle,
      'data-e2e': 'profileTabPersonal',
      render: () => (
        <ProfileDetails
          currentUser={userDetails}
          isLoadingCurrentUser={isLoadingCurrentUser}
        />
      ),
    },
    {
      value: 'assignments',
      label: t('profile.assignments', 'Assignments'),
      icon: HiOutlineClipboardDocumentCheck,
      'data-e2e': 'profileTabAssignments',
      render: () => <AssignmentsSection onSelectSite={handleSelectSite} />,
    },
    {
      value: 'notifications',
      label: t('profile.settings', 'Settings'),
      icon: HiOutlineCog6Tooth,
      'data-e2e': 'profileTabNotifications',
      render: () => (
        // Standard gap={4} section stack
        <Grid gap={4}>
          <LanguageSelector />
          <ProfileNotifications userDetails={userDetails} />
          {/* ADR-141 §5: self-service mass-notification
              destinations. Zero-surface when the org flag is off. */}
          <ProfileContact personalPhone={userDetails?.cell_phone ?? null} />
        </Grid>
      ),
    },
  ];

  // Nested site detail: SMS has no default-location, so the sub-drawer shows
  // only the per-site subscriptions — the same `ProfileSubscriptionsEditor`
  // the Assignments tab used, but scoped to the single selected site (a
  // one-entry `schools` list ⇒ site-scoped, no switcher).
  if (selectedSite && user?.id != null) {
    return (
      <DrawerComponent
        isOpen={isOpen}
        onClose={handleClose}
        title={selectedSite.name}
        e2eTitle="profileDrawer"
        size="md"
        onBack={handleBackToProfile}
        closeOnOverlayClick={false}
      >
        <Box>
          <Flex alignItems="center" justifyContent="space-between" mb={4}>
            <Text fontSize="lg" fontWeight={600} color="text.primary">
              {t('profile.subscriptions', 'Subscriptions')}
            </Text>
          </Flex>
          <ProfileSubscriptionsEditor
            userId={Number(user.id)}
            schools={[{ id: selectedSite.id, name: selectedSite.name }]}
          />
        </Box>
      </DrawerComponent>
    );
  }

  return (
    <ProfileDrawerShell
      isOpen={isOpen}
      onClose={handleClose}
      tabs={tabs}
      onSignOut={handleSignOut}
      activeTab={activeTab}
      onActiveTabChange={setActiveTab}
      footerSlot={<ResetPasswordButton userId={user?.id} />}
    />
  );
}
